import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { isUnixTimestamp } from '../../../../../core/utils/campaigns';
import { selectProviderSlice } from '../../../../../core/utils/selectProviderSlice';

/**
 * Owns the Display tab's provider slicing + range filtering.
 *
 * Extracted from CampaignsDetailsView so the regression suite can drive the
 * REAL wiring instead of a hand-copied mirror of it — a harness that
 * re-implements this logic stays green even when the production call site is
 * reverted, which is exactly how #11679 could have shipped twice.
 *
 * Preserves the locked four-anchor UTC contract (project_summary_utc_pin):
 * anchor 1 UTC row labels, anchor 2 UTC defaultDateRange, anchor 3 the picker's
 * own timezone, anchor 4 the picker->UTC-day bridge in the filter below.
 *
 * @param {object}   chartData               Page-level (aggregated) chart state.
 * @param {string}   providerIndex           'All' or a stringified index.
 * @param {Array}    byCampaignRangeDate     Picker bounds [start, end].
 * @param {Function} setDataCampaignRangeDate Writer for the KPI/chart series.
 * @returns {{displayChartData: object, campaignByDateNormalized: Array,
 *           providersTab: Array, isProviderSliceResolved: boolean}}
 */
export const useDisplayRangeFilter = (
  chartData,
  providerIndex,
  byCampaignRangeDate,
  setDataCampaignRangeDate
) => {
  const providersTab = useMemo(() => {
    return chartData && chartData.displayReports;
  }, [chartData]);

  // True only when a specific campaign tab is selected AND its report tuple
  // resolves. Distinguishes "this campaign genuinely has no delivery" from
  // "we could not tell which campaign is meant" — the two need opposite
  // fallbacks, and conflating them is what made the first fix leak.
  const isProviderSliceResolved = useMemo(() => {
    if (providerIndex === 'All' || providerIndex === undefined || providerIndex === null)
      return false;
    const parsed = parseInt(providerIndex, 10);
    if (Number.isNaN(parsed)) return false;
    return Boolean(providersTab && providersTab[parsed]);
  }, [providersTab, providerIndex]);

  // Page-level `chartData` is shared with four other tabs and
  // DisplayTabController writes it only once (its `!chartData.byDate` gate), so
  // it stays pinned to the aggregate. Slice here rather than un-freezing it.
  const displayChartData = useMemo(
    () => selectProviderSlice(chartData, providersTab, providerIndex),
    [chartData, providersTab, providerIndex]
  );

  const campaignByDateNormalized = useMemo(() => {
    if (!displayChartData || !displayChartData.byDate) return [];
    return displayChartData.byDate.map((d) => ({
      ...d,
      xAxis: isUnixTimestamp(d.xAxis)
        ? moment.unix(d.xAxis).utc().format('YYYY-MM-DD')
        : moment(new Date(d.xAxis)).utc().format('YYYY-MM-DD'),
    }));
  }, [displayChartData]);

  useEffect(() => {
    if (!byCampaignRangeDate || byCampaignRangeDate.length !== 2) return;

    if (!campaignByDateNormalized.length) {
      // Only clear for a campaign we positively resolved and that genuinely has
      // no delivery — otherwise the KPI bar would keep the PREVIOUS campaign's
      // numbers under this tab (#11679).
      //
      // When the slice did NOT resolve, leave the value alone: `chartData` can
      // legitimately be empty here (it is shared state that another tab's
      // controller may have replaced) while DisplayTabController:269 has already
      // written correct rows. Clearing unconditionally would clobber those, and
      // `dataCampaignRangeDate` is also read by Transactions/Sessions/Basket.
      if (isProviderSliceResolved) setDataCampaignRangeDate([]);
      return;
    }

    // Anchor 4 — bridge picker bounds to UTC day boundaries via day-string
    // extraction so chart and KPI bar agree on which UTC buckets are in scope.
    const startDate = moment
      .utc(moment(byCampaignRangeDate[0]).format('YYYY-MM-DD'), 'YYYY-MM-DD')
      .startOf('day');
    const endDate = moment
      .utc(moment(byCampaignRangeDate[1]).format('YYYY-MM-DD'), 'YYYY-MM-DD')
      .endOf('day');
    const filtered = campaignByDateNormalized.filter((row) => {
      const dateValue = moment.utc(row.xAxis, 'YYYY-MM-DD');
      return dateValue >= startDate && dateValue <= endDate;
    });
    setDataCampaignRangeDate(filtered);
  }, [
    byCampaignRangeDate,
    campaignByDateNormalized,
    isProviderSliceResolved,
    setDataCampaignRangeDate,
  ]);

  return {
    displayChartData,
    campaignByDateNormalized,
    providersTab,
    isProviderSliceResolved,
  };
};

export default useDisplayRangeFilter;
